/**
 * EngineRunsConsole (P147.3) -- the unified cross-module engine-batch hub.
 * Triggers + monitors every batch kind (framework obligation passes + twin
 * chain derivers + twin IBS-admission) in one place, with the Module column
 * + Cancel, reading the shared `/v1/admin/engine-runs` list. The two focused
 * consoles (obligation / twin-derive) stay as focused entry points; this is
 * the "everything + cancel" surface, and the home for the Engine-assisted
 * IBS-admission batch.
 */
import { EngineBatchRunsPanel } from "shared/admin/EngineBatchRunsPanel";
import { useTranslation } from "react-i18next";

const KINDS = [
  {
    kind: "summarise",
    name: "Summarise obligations",
    desc: "Framework · Engine: roll each obligation's controls into a requirement statement.",
  },
  {
    kind: "tag_applicability",
    name: "Tag applicability",
    desc: "Framework · Engine: tag each obligation's outcome / classification / sovereign-only.",
  },
  {
    kind: "derive_ibs_admission",
    name: "Derive IBS admission",
    desc: "Twin · Engine: derive the admission decision for every candidate IBS (the Day-0 batch).",
  },
  {
    kind: "derive_blast",
    name: "Blast radius",
    desc: "Twin: re-seal every entity's downstream blast graph.",
  },
  {
    kind: "derive_resilience",
    name: "Resilience state",
    desc: "Twin: re-derive RAG resilience from findings + dependency propagation.",
  },
  {
    kind: "derive_process",
    name: "Process coverage",
    desc: "Twin: re-seal process coverage from the customer journeys.",
  },
  {
    kind: "derive_obligation",
    name: "Obligation set",
    desc: "Twin: re-narrow + re-seal the per-entity obligation set.",
  },
];

const _OBL = "/v1/admin/framework/obligations";

function triggerPath(kind: string): string {
  if (kind === "summarise") return `${_OBL}/summarise`;
  if (kind === "tag_applicability") return `${_OBL}/tag-applicability`;
  return `/v1/admin/twin/derive-batch/${kind}`;
}

export function EngineRunsConsole() {
  const { t } = useTranslation();
  return (
    <EngineBatchRunsPanel
      testid="engine-runs-console"
      rubric="Admin · Marsad Engine"
      heading={t("screens.engineRunsConsole.engineBatchRuns")}
      intro="Trigger, watch, and cancel every catalogue-wide Engine batch in one place. Each runs in the background — safe to navigate away and come back."
      notAdminMessage="The Engine batch console is admin-only."
      kinds={KINDS}
      listPath="/v1/admin/engine-runs"
      triggerPath={triggerPath}
      showModule
    />
  );
}
